import { gql, gqlFetch, GqlError } from '@/service/gql'
import { getAuth } from '@/auth/authRef'
import { UnauthorizedError } from '@/auth/UnauthorizedError'
import { logDebug, type LogTarget, type Maybe } from '@amazon/vinyl-util'
import {
    type DevicePlaybackConcurrencyState,
    FireFlyConcurrencyErrorCode,
} from './concurrencyTypes'

const logTarget: LogTarget = {
    logPrefix: 'deviceStreamingService',
}

// language=GraphQL
const reportDeviceStreamingMutation = gql(
    `
        mutation ReportDeviceStreaming(
            $contentId: String!
            $progressInMs: Float!
            $playbackState: String!
        ) {
            reportDeviceStreaming(
                contentId: $contentId
                progressInMs: $progressInMs
                playbackState: $playbackState
            ) {
                inactivityThresholdInMs
            }
        }
    `,
    []
)

// language=GraphQL
const takeOverDeviceStreamingMutation = gql(
    `
        mutation TakeOverDeviceStreaming(
            $contentId: String!
            $progressInMs: Float!
            $playbackState: String!
        ) {
            takeOverDeviceStreaming(
                contentId: $contentId
                progressInMs: $progressInMs
                playbackState: $playbackState
            ) {
                status
            }
        }
    `,
    []
)

/**
 * Outcome of a streaming report. `MAX_CONCURRENCY_REACHED` is the only
 * error DMCS surfaces that the caller acts on (take-over prompt); every
 * other failure throws so the caller can degrade gracefully.
 */
export type ReportStreamingResult = 'OK' | 'MAX_CONCURRENCY_REACHED'

/**
 * Firefly reports a max-concurrency violation as a GraphQL error whose
 * `extensions.code` is `UPDATE_STREAMING_STATUS_ERROR` and whose
 * `extensions.payload.statusCode` is `MAX_CONCURRENCY_REACHED`, rather
 * than as a field on the response — mirrors hornet's detection in
 * `ReportDeviceStreaming.ts`.
 */
function isMaxConcurrencyError(error: unknown): boolean {
    if (!(error instanceof GqlError)) return false
    return error.errors.some((err) => {
        // `errors` rows are raw wire JSON, so `extensions` is absent on most
        // Firefly failures even though graphql-js types it as always present.
        const extensions = err.extensions as Maybe<{
            code?: unknown
            payload?: { statusCode?: unknown }
        }>
        return (
            extensions?.code === FireFlyConcurrencyErrorCode.UPDATE_STREAMING_STATUS_ERROR &&
            extensions.payload?.statusCode === FireFlyConcurrencyErrorCode.MAX_CONCURRENCY_REACHED
        )
    })
}

/**
 * Reports the current device's streaming status to DMCS. Called at
 * playback start to acquire a stream slot for the account.
 *
 * @returns `'MAX_CONCURRENCY_REACHED'` when the account is already at its
 * simultaneous-stream limit (the caller should prompt for take-over);
 * `'OK'` otherwise.
 * @throws for any non-concurrency failure (network / unexpected gateway
 * error) so the caller can decide to degrade gracefully.
 */
export async function reportDeviceStreaming(
    contentId: string,
    progressInMs: number,
    playbackState: DevicePlaybackConcurrencyState
): Promise<ReportStreamingResult> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'reportDeviceStreaming', { contentId, playbackState })
    try {
        await gqlFetch(
            reportDeviceStreamingMutation,
            { contentId, progressInMs, playbackState },
            { rejectPartialResponse: true }
        )
        return 'OK'
    } catch (error) {
        if (isMaxConcurrencyError(error)) return 'MAX_CONCURRENCY_REACHED'
        throw error
    }
}

/**
 * Terminates the account's oldest stream and claims a slot for the
 * current device. Called after the user accepts the take-over prompt.
 *
 * @throws when the gateway errors or reports a non-`SUCCESS` status, so
 * the caller can keep playback paused rather than resume into a stream
 * that was never granted.
 */
export async function takeOverDeviceStreaming(
    contentId: string,
    progressInMs: number,
    playbackState: DevicePlaybackConcurrencyState
): Promise<void> {
    if (!getAuth().isSignedIn()) throw new UnauthorizedError()
    logDebug(logTarget, 'takeOverDeviceStreaming', { contentId, playbackState })
    const result = await gqlFetch(
        takeOverDeviceStreamingMutation,
        { contentId, progressInMs, playbackState },
        { rejectPartialResponse: true }
    )
    const status = result.takeOverDeviceStreaming?.status
    logDebug(logTarget, 'takeOverDeviceStreaming result', { status })
    if (status !== 'SUCCESS') {
        throw new Error(`takeOverDeviceStreaming returned status=${status ?? 'null'}`)
    }
}
